/**
 * Marketing copy and data for the public site.
 *
 * Ported from the `ManticsCore App.dc.html` design doc (Claude Design project
 * cf836e54). The design encodes each value as an inline style string; here the
 * copy and data live as plain objects and the presentation is expressed in the
 * repo's own Tailwind + `--ios-*` token vocabulary.
 */

export type Pillar = {
  index: string;
  kicker: string;
  title: string;
  body: string;
  /** Tailwind text colour class for the kicker. */
  accent: string;
};

/** Home — the three-card "what the product does" row. */
export const PILLARS: Pillar[] = [
  {
    index: "01",
    kicker: "Share",
    title: "Anything, from any app",
    body: "Hit share, tap ManticsCore, keep scrolling. That's the whole interaction.",
    accent: "text-ios-accent-text",
  },
  {
    index: "02",
    kicker: "It files itself",
    title: "Threads that reorganize",
    // Shipped: stage_auto_rethread in the backend's ingest pipeline moves a
    // just-captured link to a closer open thread and emits link.rethreaded.
    body: "Saved links become threads. With auto-filing on, related sources can move together. Review and adjust them in your workspace.",
    accent: "text-ios-info",
  },
  {
    index: "03",
    kicker: "Publish",
    title: "One tap to a real page",
    // Shipped: named themes incl. Midnight (backend#248, DEFAULT_CLIP_THEME);
    // hide is a soft-delete / hidden flag (backend#247).
    body: "Publish a thread as a page anyone can read. Review its generated components, choose a theme, and send the link.",
    accent: "text-ios-success",
  },
];

/**
 * Home — the "front door" claims.
 *
 * These are properties of the product, not measured metrics, and that is
 * deliberate. The design doc had "3.4× readers per clip" and "11s share to
 * published"; both were invented. PostHog shows ~1.7 views per published clip,
 * and no event pair measures share-to-publish latency at all. Anything
 * numeric in this row has to come from real data, not from a design file.
 */
export const FRONT_DOOR_STATS = [
  { value: "0", label: "installs to open a clip" },
  { value: "0", label: "accounts to read one" },
  { value: "0", label: "folders to maintain" },
] as const;

/**
 * Home — the real call-to-action block at the bottom of a published clip.
 *
 * NOT marketing copy. This is the backend's `callToAction` block, appended to
 * every intent-session clip by `session_clip_blocks` in
 * `manticscore-backend/api/services/session_clip.py`. The home page mock is
 * framed as showing readers what they will actually see, so it is a fidelity
 * claim and the strings have to be the shipped ones (bairisland/manticscore#66).
 *
 * The backend treats this block as a byte-identical cross-repo contract and
 * declines to reword it without syncing iOS and web, so these values are pinned
 * against the fixture vendored at `src/test/fixtures/clip-intent-session.v1.json`
 * by `FrontDoorSection.test.tsx`. If the backend ever does reword the CTA, that
 * test fails here rather than the home page quietly going back to being wrong.
 */
export const CLIP_FOOTER = {
  heading: "Open in ManticsCore",
  body: "See the full session in the app.",
  buttonLabel: "Open app",
} as const;

export type HowStep = {
  n: string;
  title: string;
  body: string;
  detailLabel: string;
  detail: string;
  accent: string;
};

/**
 * How it works — the loop, as the pivot describes it.
 *
 * Claims below were gated on backend#244–#250 and ios#133 (web#62). Those
 * issues are closed; each line names the file that now backs it.
 *
 * Deliberately still out: "The extension saves it in about a second"
 * (manticscore-ios#134 is still open — duration is unmeasured).
 */
export const HOW_STEPS: HowStep[] = [
  {
    n: "1",
    accent: "text-ios-accent-text",
    title: "You share a link",
    // Local refusal: SharePayloadExtractor refuses mail.google.com,
    // drive.google.com, *.slack.com, icloud.com before any network handoff
    // (manticscore-ios#133). "about a second" stays out — ios#134 is open.
    body: "Share a public URL from the iPhone share sheet, or paste it into your web workspace. Pages that require your private account may not be available to capture.",
    detailLabel: "What you do",
    detail: "Share the link and let it process. Open its thread to review the result.",
  },
  {
    n: "2",
    accent: "text-ios-accent-text",
    title: "Your link becomes components",
    body: "The server processes the source content available for that link and builds components that fit it. A basic preview supports less detail than a rich public source.",
    // Capture→ready duration is now measured on link_ready.duration_ms
    // (backend#250). Skip behaviour is ingest_gate.py.
    detailLabel: "While it works",
    detail: "Processing continues on our servers. Your thread shows when a source is ready, has only a basic preview, or needs another attempt.",
  },
  {
    n: "3",
    accent: "text-ios-info",
    title: "It finds its own thread",
    // Shipped: stage_auto_rethread in api/services/ingest_pipeline.py, which
    // emits link.rethreaded so iOS can show the move.
    body: "Start a new thread or add a source to one you already have. With auto-filing enabled, related links can move to a better-matching thread.",
    detailLabel: "Why it matters",
    detail: "Review new captures in Inbox. You can adjust their placement or pause auto-filing in your account.",
  },
  {
    n: "4",
    accent: "text-ios-success",
    title: "You publish a clip",
    // Themes: backend#248 (midnight/ink/ember/forest; Midnight is default).
    // Per-component regenerate + tone: #246. Hide + field edit: #247.
    body: "Publish a thread as a public page with its generated components. Review it, change the theme, or hide a component before sharing the link.",
    detailLabel: "What readers get",
    detail: "A real page, Midnight-themed by default, with no install, no account, and a footer that lets them start their own.",
  },
  {
    n: "5",
    accent: "text-ios-success",
    title: "It keeps working",
    // Clip refresh: session_link_removed republishes a published clip after
    // a link leaves (backend#244). Stale flag on GET /v1/threads (#249).
    body: "Add a source later and the published clip updates as processing finishes. Quiet threads are marked in your workspace so you can return to them.",
    detailLabel: "Ongoing",
    detail: "You can keep adding to a published thread, or unpublish its clip whenever you choose.",
  },
];

/** How it works — the eight component types a link can be written into. */
export const COMPONENT_TYPES = [
  { label: "Hero", desc: "Cover image and headline" },
  { label: "Summary", desc: "What the page argues, in a paragraph" },
  { label: "Quote", desc: "Pulled straight from the source" },
  { label: "Key points", desc: "Bulleted takeaways" },
  { label: "Comparison", desc: "How it stacks against alternatives" },
  { label: "Steps", desc: "Numbered how-to" },
  { label: "Related", desc: "Other links worth a look" },
  { label: "Call to action", desc: "Button out to the source" },
] as const;

/**
 * How it works — capabilities of the existing MCP integration (backend#245).
 *
 * MCP tools on /mcp, scoped by services.mcp_scopes.VALID_SCOPES
 * (threads / links / search / write). write is withheld by default.
 * Tokens are minted and revoked via routes/mcp_tokens.py.
 */
export const MCP_CAPABILITIES = [
  "Search every thread you have saved",
  "Pull any link's generated components",
  "Read a thread and its generated cards",
  "Write access stays off unless you turn it on",
] as const;

/**
 * The design doc's 9-clip gallery (invented titles, authors and open counts) and
 * its topic filter list lived here to feed a /clips page. That page was folded
 * into /spotlight, which runs on the real spotlight dataset, so the fabricated
 * rows are deleted rather than left lying around to be picked up again.
 */

/**
 * UNAPPROVED — these figures came from a design doc, not from a pricing
 * decision, and contradict what the iOS app sells (Explorer $9.99 / Builder
 * $19.99 / Pro $39.99 / Team $99.99 plus credit packs). PLANS, ATTRIBUTION_EXAMPLE
 * and the billing answers in FAQS are consumed only by the unrouted PricingPage.
 * Replace with approved numbers before that page is published again.
 */
export type Plan = {
  name: string;
  price: string;
  per: string;
  blurb: string;
  cta: string;
  featured: boolean;
  volume: string;
  dist: string;
  features: string[];
};

export const PLANS: Plan[] = [
  {
    name: "Reader",
    price: "$0",
    per: "forever",
    volume: "1,000 clip views",
    dist: "Open counts",
    blurb: "For saving your own reading and sharing it with a few people.",
    cta: "Get the app",
    featured: false,
    features: [
      "1,000 clip views a month",
      "50 links captured a month",
      "3 clips live at once",
      "Open counts, last 7 days",
      "Midnight theme",
    ],
  },
  {
    name: "Creator",
    price: "$12",
    per: "/month",
    volume: "25,000 clip views",
    dist: "Click analytics",
    blurb: "For anyone whose links are the point — affiliates, newsletters, curators.",
    cta: "Start free trial",
    featured: true,
    features: [
      "25,000 clip views a month",
      "500 links captured a month",
      "Click-through per link, not just per page",
      "Affiliate links and UTM passthrough",
      "Referrers, geography, device",
      "Custom slugs on manticscore.com",
      "Unlimited live clips",
    ],
  },
  {
    name: "Partner",
    price: "$39",
    per: "/month",
    volume: "250,000 clip views",
    dist: "Revenue attribution",
    blurb: "For clips that carry real traffic and need to prove what they earned.",
    cta: "Start free trial",
    featured: false,
    features: [
      "250,000 clip views a month",
      "5,000 links captured a month",
      "Revenue attributed to each link",
      "A/B two versions of a clip",
      "Email capture on clip pages",
      "Publish to your own domain",
      "Shared threads for up to 5 people",
    ],
  },
];

/** Pricing — the worked attribution example. */
export const ATTRIBUTION_EXAMPLE = {
  clipTitle: "Standing desks under $400",
  views: "4,182 views",
  total: "$412.60",
  rows: [
    { label: "Uplift V2 review — theverge.com", clicks: "611", revenue: "$188.40", pct: 100 },
    { label: "Monitor arm — amazon.com", clicks: "402", revenue: "$142.10", pct: 66 },
    { label: "Standing mat — amazon.com", clicks: "188", revenue: "$61.90", pct: 31 },
    { label: "Teardown notes — ifixit.com", clicks: "74", revenue: "$20.20", pct: 12 },
  ],
} as const;

export const FAQS = [
  {
    q: "Do readers need the app to open a clip?",
    a: "No. Clips are ordinary web pages. No install, no account, no paywall — that is the point of them.",
  },
  {
    // Local refusal shipped in ios#133: mail.google.com, drive.google.com,
    // *.slack.com, icloud.com are declined in SharePayloadExtractor before
    // any network handoff. Sign-in / paywall pages are still a server-side
    // gate (ingest_gate.py) after a public URL is fetched.
    q: "What happens to links that can't be captured?",
    a: "Nothing is stored. Mail, Drive and Slack links are declined at the extension and never leave your device. Public pages that resolve to a sign-in or a paywall are declined on our side rather than wrapped.",
  },
  {
    // Per-component regenerate + tone (shorter / more_detail / punchier): #246.
    // Field-level PATCH including comparison rows: #247.
    q: "Can I fix something the AI got wrong?",
    a: "Yes. Every component can be regenerated with a different tone, or edited field by field — including individual rows in a list.",
  },
  {
    q: "What counts as a clip view?",
    a: "One person opening one of your public clip pages. Your own visits, bots and prefetches are excluded, and a reader refreshing counts once an hour.",
  },
  {
    q: "What happens if a clip goes viral?",
    a: "Nothing breaks and nothing goes dark. You keep serving, and overage bills at $1 per extra 1,000 views — capped at the next plan's price, so a good week never costs more than upgrading.",
  },
  {
    q: "Do you touch my affiliate links?",
    a: "No. Parameters and redirects pass through exactly as you saved them. We only count the click and attribute it to that link.",
  },
  {
    q: "Does the free plan expire?",
    a: "No. A thousand views a month, indefinitely. Paid plans exist for reach and the numbers behind it, not to unlock the basics.",
  },
  {
    q: "Who can see my private threads?",
    a: "Your threads stay private unless you publish a clip or grant access through MCP. MCP access follows the permissions you choose and can be revoked in the app.",
  },
] as const;
